import * as React from 'react';
import useIsomorphicLayoutEffect from '../hooks/useIsomorphicLayoutEffect';
import Head from './TableHead';
import HeadRow from './TableHeadRow';
import Column from './TableCol';
import ColumnCheckbox from './TableColCheckbox';
import ColumnExpander from './TableColExpander';
import RightPinSpacer from './RightPinSpacer';
import { CellBase } from './Cell';
import { buildGridTemplateColumns, buildGroupHeaderCells, type GroupDragProps } from './dataTableHeadHelpers';
import { getFirstRightPinnedId, getPrefixColCount } from '../util';
import { flipElement } from '../dom';
import type { TableColumn, ColumnGroup } from '../types';
import { emptyFilterState } from '../hooks/useColumnFilter';
import { useHeadContext } from '../context/HeadContext';

interface DataTableHeadProps<T> {
	columns: TableColumn<T>[];
	columnGroups?: ColumnGroup[];
	selectableRows: boolean;
	expandableRows: boolean;
	expandableRowsHideExpander: boolean;
}

function DataTableHead<T>({
	columns,
	columnGroups,
	selectableRows,
	expandableRows,
	expandableRowsHideExpander,
}: DataTableHeadProps<T>): JSX.Element {
	const {
		sorting,
		selectAll,
		fixedHeader,
		dense,
		draggingColumnId,
		draggingGroupKey,
		filtering,
		columnWidths,
		pinnedOffsets,
		resize,
		cellNavigation,
		activeCell,
		headerMenu,
		columnDrag,
	} = useHeadContext<T>();

	const groupDragProps: GroupDragProps = {
		draggingGroupKey,
		onGroupDragStart: columnDrag.onGroupDragStart,
		onGroupDragEnter: columnDrag.onGroupDragEnter,
		onGroupDragOver: columnDrag.onGroupDragOver,
		onGroupDragEnd: columnDrag.onGroupDragEnd,
		onGroupPointerDown: columnDrag.onGroupPointerDown,
	};

	const visibleColumns = columns.filter(c => !c.omit);
	const hasGroups = columnGroups && columnGroups.length > 0;

	// ── FLIP animation for column/group reorder ──────────────────────────────
	const containerRef = React.useRef<HTMLDivElement>(null);
	const savedPositions = React.useRef<Map<string, number>>(new Map());
	const isMounted = React.useRef(false);
	const columnOrder = React.useMemo(() => visibleColumns.map(c => c.id).join(','), [visibleColumns]);
	const groupOrder = React.useMemo(() => (columnGroups ?? []).map(g => g.columnIds[0]).join(','), [columnGroups]);

	useIsomorphicLayoutEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		const animate = isMounted.current;
		isMounted.current = true;

		const tryFlip = (el: HTMLElement, key: string) => {
			const newLeft = el.getBoundingClientRect().left;
			const prevLeft = savedPositions.current.get(key);
			savedPositions.current.set(key, newLeft);
			if (!animate || reducedMotion || prevLeft == null || Math.abs(prevLeft - newLeft) < 1) return;

			flipElement(el, prevLeft - newLeft, 'X', 0.2);
		};

		container.querySelectorAll<HTMLElement>('[data-column-id]').forEach(el => {
			tryFlip(el, `col:${el.dataset.columnId}`);
		});
		container.querySelectorAll<HTMLElement>('[data-group-key]').forEach(el => {
			tryFlip(el, `grp:${el.dataset.groupKey}`);
		});

		const positions = savedPositions.current;
		const mounted = isMounted;

		return () => {
			mounted.current = false;
			positions.clear();
		};
	}, [columnOrder, groupOrder]);

	// Count of non-omitted columns each group spans
	const groupColSpans = React.useMemo(() => {
		if (!columnGroups) return {};
		const map: Record<string, number> = {};
		for (const group of columnGroups) {
			map[String(group.name)] = group.columnIds.filter(id =>
				visibleColumns.some(c => String(c.id) === String(id)),
			).length;
		}
		return map;
	}, [columnGroups, visibleColumns]);

	// IDs of columns not covered by any group
	const ungroupedIds = React.useMemo(() => {
		if (!columnGroups) return new Set<string>();
		const covered = new Set(([] as (string | number)[]).concat(...columnGroups.map(g => g.columnIds)).map(String));
		return new Set(visibleColumns.map(c => String(c.id)).filter(id => !covered.has(id)));
	}, [columnGroups, visibleColumns]);

	const prefixColCount = getPrefixColCount(selectableRows, expandableRows, expandableRowsHideExpander);

	// ── Shared column props ──────────────────────────────────────────────────
	const colProps = (column: TableColumn<T>) => ({
		column,
		disabled: sorting.sortDisabled,
		sorting,
		// Per-column extraction keeps TableCol's memo per-column: only the filtered
		// column re-renders when a filter is applied.
		filterValue: filtering.filterValues[column.id!] ?? emptyFilterState(column.filterType),
		filterLocalization: filtering.localization,
		resizedWidth: columnWidths[column.id!],
		onFilterChange: filtering.onFilterChange,
		onResizeStart: resize?.onResizeStart,
		pinnedOffsets,
		columnDrag,
		draggingColumnId,
		cellNavigation,
		activeCell,
		navCol: prefixColCount + visibleColumns.indexOf(column),
		headerMenu,
	});

	const firstRightPinnedId = React.useMemo(() => getFirstRightPinnedId(columns), [columns]);

	// ── CSS Grid layout (when columnGroups are present) ──────────────────────
	if (hasGroups) {
		const gridTemplateColumns = buildGridTemplateColumns(visibleColumns, prefixColCount, columnWidths);

		// Expander column index (1-based grid column)
		let expanderGridCol = 0;
		if (selectableRows) expanderGridCol = 2;
		else if (expandableRows && !expandableRowsHideExpander) expanderGridCol = 1;

		return (
			<Head className="rdt_TableHead" role="rowgroup" $fixedHeader={fixedHeader}>
				<div
					ref={containerRef}
					className={['rdt_headGrid', dense && 'rdt_headGridDense'].filter(Boolean).join(' ')}
					role="presentation"
					style={{ gridTemplateColumns }}
				>
					{/* ── Prefix cells — span both grid rows ── */}
					{selectableRows && (
						<div style={{ gridColumn: '1', gridRow: '1 / span 2', display: 'flex', alignItems: 'stretch' }}>
							{selectAll?.hideSelectAll ? (
								<CellBase style={{ flex: '0 0 var(--rdt-system-col-width, 48px)', width: '100%' }} />
							) : (
								<ColumnCheckbox />
							)}
						</div>
					)}
					{expandableRows && !expandableRowsHideExpander && (
						<div
							style={{
								gridColumn: String(expanderGridCol),
								gridRow: '1 / span 2',
								display: 'flex',
								alignItems: 'stretch',
							}}
						>
							<ColumnExpander />
						</div>
					)}

					{/*
					 * ── Column header cells ──
					 * Rendered BEFORE group label cells in the DOM so that the CSS adjacent-
					 * sibling selector (.rdt_cellBaseHead + .rdt_cellBaseHead::before) is not
					 * broken by group cells appearing between column cells in DOM order.
					 * Visual placement is controlled entirely by gridColumn / gridRow.
					 */}
					{visibleColumns.map((column, visIdx) => {
						const gridCol = prefixColCount + visIdx + 1;
						const isUngrouped = ungroupedIds.has(String(column.id));
						// In grid mode the track width controls layout — resizedWidth inline styles
						// would conflict with grid-template-columns, so we strip it here.
						const { resizedWidth: _skip, ...rest } = colProps(column);
						return (
							<Column
								key={column.id}
								{...rest}
								gridStyle={{
									gridColumn: String(gridCol),
									gridRow: isUngrouped ? '1 / span 2' : '2',
								}}
							/>
						);
					})}

					{/* ── Group label cells (row 1) — rendered last in DOM ── */}
					{buildGroupHeaderCells(
						visibleColumns,
						columnGroups!,
						ungroupedIds,
						groupColSpans,
						prefixColCount,
						groupDragProps,
					)}
				</div>
			</Head>
		);
	}

	// ── Standard flex layout (no columnGroups) ──────────────────────────────
	return (
		<Head className="rdt_TableHead" role="rowgroup" $fixedHeader={fixedHeader}>
			<HeadRow ref={containerRef} className="rdt_TableHeadRow" role="row" $dense={dense}>
				{selectableRows &&
					(selectAll?.hideSelectAll ? (
						<CellBase style={{ flex: '0 0 var(--rdt-system-col-width, 48px)' }} />
					) : (
						<ColumnCheckbox />
					))}

				{expandableRows && !expandableRowsHideExpander && <ColumnExpander />}

				{columns.map(column => (
					<React.Fragment key={column.id}>
						{firstRightPinnedId != null && column.id === firstRightPinnedId && <RightPinSpacer />}
						<Column {...colProps(column)} />
					</React.Fragment>
				))}
			</HeadRow>
		</Head>
	);
}

export default DataTableHead;
